import * as React from "react";
import { cn } from "../utils";

/**
 * The plain section — whitespace-not-boxes (docs/design/prototypes/redesign/SYSTEM.md §3.2).
 *
 * `SectionLabel` is the ONE eyebrow: a tiny uppercase muted label. Every
 * box-less section on every page uses it, so section headings share one voice
 * and one left edge. `Section` = the eyebrow (+ an optional muted `aside` on
 * the same baseline, e.g. "ordered by urgency") + the content, with `space-y-2`.
 * Nothing here is inset, so the content sits on the grid line under the eyebrow
 * — a `TaskList` or a hung `Ledger` placed below lines up exactly.
 *
 * Ledger labels are eyebrows ABOVE the card (a `Section` wrapping a `Ledger`),
 * not an integrated card title — so "Portfolio"/"Runs" read exactly like
 * "Tasks" and share the grid line with the ledger's content.
 */
export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="type-subtext font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </h2>
  );
}

export function Section({
  label,
  aside,
  action,
  className,
  children,
}: {
  label: React.ReactNode;
  /** Muted clause on the eyebrow's baseline (e.g. "ordered by urgency"). */
  aside?: React.ReactNode;
  /** Right-aligned quiet link on the eyebrow's baseline — the section's "see all"
   *  doorway (e.g. "All issues →"). Pass a `<Link variant="muted">`. */
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={cn("space-y-2", className)}>
      {aside != null || action != null ? (
        <div className="flex items-baseline gap-2">
          <SectionLabel>{label}</SectionLabel>
          {aside != null ? (
            <span className="type-subtext text-muted-foreground/70">{aside}</span>
          ) : null}
          {action != null ? (
            <span className="ml-auto type-subtext">{action}</span>
          ) : null}
        </div>
      ) : (
        <SectionLabel>{label}</SectionLabel>
      )}
      {children}
    </section>
  );
}
